/*
 * Brand test, part B: the 5-step size finder ("Find din størrelse").
 *
 * - Pop-up: <dialog data-size-finder-dialog> from snippets/size-finder-dialog.liquid (rendered once by
 *   layout/theme.liquid). Any element with [data-size-finder-open], or a link to /pages/find-din-stoerrelse,
 *   opens it. Without JS those links simply go to the page.
 * - Inline: [data-size-finder-app][data-size-finder-inline] on the page find-din-stoerrelse
 *   (sections/size-finder-page.liquid). There the same triggers start the guide in the page instead.
 * - Data: assets/size-finder.json, fetched once, when the guide first opens. It is built from the test's
 *   size-finder.json by size-finder-tools/build_size_finder_data.py (local build folder, not in the theme).
 *
 * The resolver below is pure. The offline test (size-finder-tools/test_size_finder_routes.js) loads this
 * file in node and runs every route through it, so the browser and the test use the same code.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- resolver (pure) */

  var CONTACT = { kind: 'contact' };

  function has(obj, key) {
    return obj !== null && typeof obj === 'object' && Object.prototype.hasOwnProperty.call(obj, key);
  }

  function lowEnd(bandId) {
    return parseInt(String(bandId), 10);
  }

  /** Route key: location|floor|shape|leg ("" for wheels, which skip step 4). */
  function routeKey(a) {
    return [a.location, a.floor, a.shape, a.leg || ''].join('|');
  }

  /** The route for answers 1–4: {line, mode, fallback?}, or null when it ends on "contact us". */
  function routeFor(data, a) {
    var key = routeKey(a);
    return has(data.routes, key) ? data.routes[key] : null;
  }

  /** Width × depth, swapped when the buyer gave a depth bigger than the width. */
  function orderedPair(w, d) {
    return lowEnd(d) > lowEnd(w) ? [d, w] : [w, d];
  }

  function fromCell(data, cell) {
    if (!cell || typeof cell.r !== 'number') return null;
    var res = data.results[cell.r];
    if (!res) return null;
    return {
      kind: 'match',
      sku: res.sku,
      handle: res.handle,
      title: res.title,
      size: res.size,
      pack: res.pack,
      colour: res.colour,
      price: res.price,
      variant: res.variant,
      img: res.img,
      stretch: cell.stretch === true,
      url: '/products/' + res.handle + '?variant=' + res.variant,
    };
  }

  /**
   * Answers → outcome. a = {location, floor, shape, leg, band, w, d, pin}.
   * Returns {kind: 'match', ...}, {kind: 'contact'}, or null when the step-5 answer is still missing.
   */
  function resolve(data, a) {
    var route = routeFor(data, a);
    if (!route) return CONTACT;
    var cell = null;

    if (route.mode === 'band' || route.mode === 'tube_band') {
      if (!a.band) return null;
      if (has(data.bands[route.line], a.band)) cell = data.bands[route.line][a.band];
      if (!cell && route.fallback && has(data.bands[route.fallback], a.band)) {
        cell = data.bands[route.fallback][a.band];
      }
    } else if (route.mode === 'width_depth') {
      if (!a.w || !a.d) return null;
      var pair = orderedPair(a.w, a.d);
      var key = pair[0] + 'x' + pair[1];
      if (has(data.pairs[route.line], key)) cell = data.pairs[route.line][key];
      if (cell && typeof cell.r !== 'number') cell = has(cell, a.leg) ? cell[a.leg] : null;
    } else if (route.mode === 'pin') {
      if (!a.pin) return null;
      if (has(data.pins, a.pin)) cell = data.pins[a.pin];
    }

    return fromCell(data, cell) || CONTACT;
  }

  var core = { resolve: resolve, routeFor: routeFor, routeKey: routeKey, orderedPair: orderedPair };

  if (typeof module === 'object' && module && module.exports) {
    module.exports = core;
    return;
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  /* ---------------------------------------------------------------- interface */

  var PAGE_PATH = /\/pages\/find-din-stoerrelse\/?$/;
  var STEP_NO = { location: 1, floor: 2, shape: 3, leg: 4, size: 5, result: 6 };
  var SIZE_KEYS = ['band', 'w', 'd', 'pin'];

  // Interface words that are not part of the store copy (labels for icons, loading and error states).
  var T = {
    back: 'Tilbage',
    close: 'Luk',
    step: function (n) {
      return 'Trin ' + n + ' af 5: ';
    },
    loading: 'Henter guiden …',
    error: 'Guiden kunne ikke hentes.',
    retry: 'Prøv igen',
    restart: 'Start forfra',
    show: 'Vis resultat',
    width: 'Bredde – den lange side',
    depth: 'Dybde – den korte side',
    size: 'Størrelse',
    widthShort: 'Bredde',
    depthShort: 'dybde',
    pin: 'Stift',
    kr: ' kr',
  };

  var SVG_OPEN = '<svg class="sf-svg" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" ';
  var ICON_BACK =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square"><path d="M20 12H5M11 5l-7 7 7 7"/></svg>';
  var ICON_CLOSE =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square"><path d="M5 5l14 14M19 5L5 19"/></svg>';
  var ICON_TICK =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8.5l3.2 3L13 4.5"/></svg>';

  // Step 3: the bottom of the leg, seen from below.
  var SHAPE_SVG = {
    rund: '<circle cx="32" cy="32" r="20"/>',
    firkantet: '<rect x="13" y="13" width="38" height="38"/>',
    rektangulaer: '<rect x="7" y="19" width="50" height="26"/>',
    oval: '<ellipse cx="32" cy="32" rx="25" ry="15"/>',
    staalroer: '<circle cx="32" cy="32" r="20"/><circle class="sf-svg-hole" cx="32" cy="32" r="12"/>',
    hjul:
      '<path class="sf-svg-line" d="M32 4v10"/><path d="M21 14h22v9H21z"/><circle cx="32" cy="41" r="16"/><circle class="sf-svg-dot" cx="32" cy="41" r="3.5"/>',
  };

  // Step 4: the leg from the side. The dashed line is plumb, so the slant reads at a glance.
  var FLOOR = '<path class="sf-svg-line" d="M4 74h56"/>';
  var TUBE_FRAME =
    '<path class="sf-svg-tube" d="M6 70H40a14 14 0 0 0 14-14V6"/><path class="sf-svg-tube-in" d="M6 70H40a14 14 0 0 0 14-14V6"/>';
  var LEG_SVG = {
    lige: FLOOR + '<path class="sf-svg-guide" d="M32 4v70"/><path d="M28 8h8v66h-8z"/>',
    let: FLOOR + '<path class="sf-svg-guide" d="M37 4v70"/><path d="M22 8h8l11 66h-8z"/>',
    meget: FLOOR + '<path class="sf-svg-guide" d="M52 4v70"/><path d="M11 8h8l37 66h-8z"/>',
    rundt_roer:
      TUBE_FRAME + '<circle cx="20" cy="26" r="11"/><circle class="sf-svg-hole" cx="20" cy="26" r="6"/>',
    firkantet_roer:
      TUBE_FRAME + '<rect x="9" y="15" width="22" height="22"/><rect class="sf-svg-hole" x="14" y="20" width="12" height="12"/>',
  };

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var uidCount = 0;
  function uid(prefix) {
    uidCount += 1;
    return 'sf-' + prefix + '-' + uidCount;
  }

  function withRoot(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + path;
  }

  function labelOf(list, id) {
    for (var i = 0; i < (list || []).length; i++) if (list[i].id === id) return list[i].label;
    return '';
  }

  /** "Let skrå (op til ca. 15°)" → main text + the bracket on its own line. */
  function labelHtml(label) {
    var m = /^(.*?)\s*(\([^)]*\))$/.exec(label);
    if (!m) return esc(label);
    return esc(m[1]) + '<span class="sf-choice__sub">' + esc(m[2]) + '</span>';
  }

  function imgTag(data, index, sizes, cls) {
    var im = data.imgs[index];
    if (!im) return '';
    var url = im.url;
    var join = url.indexOf('?') === -1 ? '?' : '&';
    var srcset = [240, 360, 480, 720, 960]
      .map(function (w) {
        return url + join + 'width=' + w + ' ' + w + 'w';
      })
      .join(', ');
    return (
      '<img class="' + (cls || '') + '" src="' + esc(url + join + 'width=480') + '" srcset="' + esc(srcset) +
      '" sizes="' + esc(sizes) + '" alt="' + esc(im.alt) + '" loading="lazy" decoding="async">'
    );
  }

  /* --- data: one fetch per page view, shared by the pop-up and the inline guide --- */

  var dataPromise = null;
  function loadData(src) {
    if (!dataPromise) {
      dataPromise = fetch(src, { credentials: 'same-origin' }).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      });
      dataPromise.catch(function () {
        dataPromise = null; // a later open may try again
      });
    }
    return dataPromise;
  }

  /* --- one guide (pop-up or inline) --- */

  function Finder(root, options) {
    this.root = root;
    this.src = options.src;
    this.dialog = options.dialog || null;
    this.data = null;
    this.a = {};
    this.history = [];
    this.screen = null;
    this.built = false;
    root.addEventListener('click', this.onClick.bind(this));
  }

  Finder.prototype.build = function () {
    if (this.built) return;
    this.built = true;
    var dots = '';
    for (var i = 0; i < 5; i++) dots += '<li></li>';
    this.root.innerHTML =
      '<div class="sf">' +
      '<div class="sf__bar">' +
      '<button type="button" class="sf__icon" data-sf-back aria-label="' + T.back + '">' + ICON_BACK + '</button>' +
      '<ol class="sf__dots" aria-hidden="true">' + dots + '</ol>' +
      (this.dialog
        ? '<button type="button" class="sf__icon" data-sf-close aria-label="' + T.close + '">' + ICON_CLOSE + '</button>'
        : '<span class="sf__icon" aria-hidden="true"></span>') +
      '</div>' +
      '<div class="sf__body" data-sf-body></div>' +
      '</div>';
    this.body = this.root.querySelector('[data-sf-body]');
    this.backBtn = this.root.querySelector('[data-sf-back]');
    this.dots = this.root.querySelectorAll('.sf__dots li');
  };

  Finder.prototype.reset = function () {
    this.a = {};
    this.history = [];
  };

  /** Open the guide: a fresh start, unless it was left half-way (then it carries on there). */
  Finder.prototype.start = function () {
    var self = this;
    this.build();
    if (this.data) {
      if (!this.screen || this.screen === 'result') {
        this.reset();
        this.show('location');
      } else {
        this.focusHeading();
      }
      return;
    }
    this.renderStatus(false);
    loadData(this.src).then(
      function (data) {
        self.data = data;
        self.reset();
        self.show('location');
      },
      function () {
        self.renderStatus(true);
      }
    );
  };

  Finder.prototype.renderStatus = function (failed) {
    this.screen = null;
    this.backBtn.style.visibility = 'hidden';
    this.body.innerHTML = failed
      ? '<div class="sf__status" role="alert"><p>' + T.error + '</p>' +
        '<button type="button" class="sf-btn sf-btn--secondary" data-sf-retry>' + T.retry + '</button></div>'
      : '<div class="sf__status" role="status"><span class="sf-spinner" aria-hidden="true"></span>' + T.loading + '</div>';
  };

  Finder.prototype.focusHeading = function () {
    var heading = this.body && this.body.querySelector('[data-sf-focus]');
    if (heading) heading.focus();
  };

  Finder.prototype.go = function (screen) {
    if (this.screen) this.history.push(this.screen);
    this.show(screen);
  };

  Finder.prototype.back = function () {
    if (!this.history.length) return;
    this.show(this.history.pop());
  };

  Finder.prototype.show = function (screen) {
    this.screen = screen;
    var html;
    if (screen === 'location') html = this.photoStep('location', 1, 'sf-choices--photo', '(min-width: 750px) 290px, 45vw');
    else if (screen === 'floor') html = this.photoStep('floor', 2, 'sf-choices--rows', '96px');
    else if (screen === 'shape') html = this.shapeStep();
    else if (screen === 'leg') html = this.legStep();
    else if (screen === 'size') html = this.sizeStep();
    else html = this.resultScreen();

    this.body.innerHTML = html;
    this.body.scrollTop = 0;
    this.updateBar();
    this.focusHeading();
  };

  Finder.prototype.updateBar = function () {
    var n = STEP_NO[this.screen] || 1;
    this.backBtn.style.visibility = this.history.length ? 'visible' : 'hidden';
    for (var i = 0; i < this.dots.length; i++) {
      this.dots[i].className = i + 1 < n ? 'is-done' : i + 1 === n ? 'is-current' : '';
    }
  };

  Finder.prototype.head = function (n, question, help) {
    var id = uid('q');
    return {
      id: id,
      html:
        '<h2 class="sf__q" id="' + id + '" tabindex="-1" data-sf-focus><span class="sf-vh">' + T.step(n) + '</span>' +
        esc(question) + '</h2>' + (help ? '<p class="sf__help">' + esc(help) + '</p>' : ''),
    };
  };

  Finder.prototype.choice = function (step, c, media) {
    var id = uid('c');
    var on = this.a[step] === c.id;
    return (
      '<button type="button" class="sf-choice" data-sf-step="' + step + '" data-sf-value="' + esc(c.id) +
      '" aria-pressed="' + on + '" aria-labelledby="' + id + '">' + (media || '') +
      '<span class="sf-choice__label" id="' + id + '">' + labelHtml(c.label) + '</span></button>'
    );
  };

  Finder.prototype.photoStep = function (step, n, cls, sizes) {
    var s = this.data.steps[step];
    var head = this.head(n, s.q, s.help);
    var self = this;
    var items = s.choices
      .map(function (c) {
        var media = c.img != null ? '<span class="sf-choice__media">' + imgTag(self.data, c.img, sizes) + '</span>' : '';
        return self.choice(step, c, media);
      })
      .join('');
    return (
      '<div class="sf__screen">' + head.html + '<div class="sf-choices ' + cls + '" role="group" aria-labelledby="' +
      head.id + '">' + items + '</div></div>'
    );
  };

  Finder.prototype.shapeStep = function () {
    var s = this.data.steps.shape;
    var head = this.head(3, s.q, s.help);
    var self = this;
    var items = s.choices
      .map(function (c) {
        var art = SHAPE_SVG[c.id] ? '<span class="sf-choice__art">' + SVG_OPEN + 'viewBox="0 0 64 64">' + SHAPE_SVG[c.id] + '</svg></span>' : '';
        return self.choice('shape', c, art);
      })
      .join('');
    return (
      '<div class="sf__screen">' + head.html + '<div class="sf-choices sf-choices--icons" role="group" aria-labelledby="' +
      head.id + '">' + items + '</div></div>'
    );
  };

  Finder.prototype.legStep = function () {
    var s = this.data.steps.leg;
    var head = this.head(4, s.q, s.help);
    var self = this;
    var items = (s.by_shape[this.a.shape] || [])
      .map(function (c) {
        var art = LEG_SVG[c.id] ? '<span class="sf-choice__art">' + SVG_OPEN + 'viewBox="0 0 64 80">' + LEG_SVG[c.id] + '</svg></span>' : '';
        return self.choice('leg', c, art);
      })
      .join('');
    return (
      '<div class="sf__screen">' + head.html + '<div class="sf-choices sf-choices--legs" role="group" aria-labelledby="' +
      head.id + '">' + items + '</div></div>'
    );
  };

  Finder.prototype.mmGrid = function (key, list, labelledBy) {
    var self = this;
    var items = list
      .map(function (c) {
        return (
          '<button type="button" class="sf-mm" data-sf-step="' + key + '" data-sf-value="' + esc(c.id) + '" aria-pressed="' +
          (self.a[key] === c.id) + '">' + esc(c.label) + '</button>'
        );
      })
      .join('');
    return '<div class="sf-grid" role="group" aria-labelledby="' + labelledBy + '">' + items + '</div>';
  };

  Finder.prototype.sizeStep = function () {
    var route = routeFor(this.data, this.a);
    var mode = this.data.steps.size.modes[route.mode];
    var head = this.head(5, mode.q, mode.help);
    var photo = mode.img != null ? '<div class="sf-measure">' + imgTag(this.data, mode.img, '(min-width: 750px) 600px, 100vw') + '</div>' : '';
    var html = '<div class="sf__screen">' + head.html + photo;

    if (route.mode === 'width_depth') {
      var wId = uid('w');
      var dId = uid('d');
      html +=
        '<h3 class="sf-sub" id="' + wId + '">' + T.width + '</h3>' + this.mmGrid('w', mode.w, wId) +
        '<h3 class="sf-sub" id="' + dId + '">' + T.depth + '</h3>' + this.mmGrid('d', mode.d, dId) +
        '<button type="button" class="sf-btn sf-btn--primary sf-show" data-sf-show' +
        (this.a.w && this.a.d ? '' : ' disabled') + '>' + T.show + '</button>';
    } else {
      var key = route.mode === 'pin' ? 'pin' : 'band';
      html += this.mmGrid(key, mode.choices, head.id);
    }
    return html + '</div>';
  };

  Finder.prototype.ticks = function (route) {
    var s = this.data.steps;
    var a = this.a;
    var legLabel = labelOf(s.leg.by_shape[a.shape], a.leg);
    var shape = labelOf(s.shape.choices, a.shape);
    var lines = [
      labelOf(s.location.choices, a.location) + ' · ' + labelOf(s.floor.choices, a.floor),
      legLabel ? shape + ' · ' + legLabel : shape,
    ];
    var mode = s.size.modes[route.mode];
    if (route.mode === 'width_depth') {
      var pair = orderedPair(a.w, a.d);
      lines.push(T.widthShort + ' ' + labelOf(mode.w, pair[0]) + ' × ' + T.depthShort + ' ' + (labelOf(mode.d, pair[1]) || labelOf(mode.w, pair[1])));
    } else if (route.mode === 'pin') {
      lines.push(T.pin + ' ' + labelOf(mode.choices, a.pin));
    } else {
      lines.push(labelOf(mode.choices, a.band));
    }
    return (
      '<ul class="sf-ticks">' +
      lines
        .map(function (line) {
          return '<li><span class="sf-tick">' + ICON_TICK + '</span><span>' + esc(line) + '</span></li>';
        })
        .join('') +
      '</ul>'
    );
  };

  Finder.prototype.resultScreen = function () {
    var outcome = resolve(this.data, this.a);
    var restart = '<button type="button" class="sf-btn sf-btn--secondary" data-sf-restart>' + T.restart + '</button>';

    if (!outcome || outcome.kind !== 'match') {
      var nm = this.data.text.nomatch;
      return (
        '<div class="sf__screen sf__screen--result">' +
        '<h2 class="sf__q" tabindex="-1" data-sf-focus>' + esc(nm.heading) + '</h2>' +
        '<p class="sf__lead">' + esc(nm.text) + '</p>' +
        '<a class="sf-btn sf-btn--primary" href="' + esc(withRoot(nm.url)) + '">' + esc(nm.button) + '</a>' +
        restart + '</div>'
      );
    }

    var rt = this.data.text.result;
    var route = routeFor(this.data, this.a);
    return (
      '<div class="sf__screen sf__screen--result">' +
      '<h2 class="sf__q" tabindex="-1" data-sf-focus>' + esc(rt.heading) + '</h2>' +
      '<div class="sf-card">' +
      '<div class="sf-card__media">' + imgTag(this.data, outcome.img, '120px') + '</div>' +
      '<div class="sf-card__body">' +
      '<p class="sf-card__name">' + esc(outcome.title) + '</p>' +
      '<p class="sf-card__size">' + T.size + ': ' + esc(outcome.size) + '</p>' +
      '<p class="sf-card__pack">' + esc(outcome.pack) + ' · ' + esc(outcome.colour) + '</p>' +
      '</div></div>' +
      (outcome.stretch ? '<p class="sf-note">' + esc(rt.stretch_note) + '</p>' : '') +
      this.ticks(route) +
      '<p class="sf-price">' + esc(outcome.price) + T.kr + '</p>' +
      '<a class="sf-btn sf-btn--primary" href="' + esc(withRoot(outcome.url)) + '">' + esc(rt.button) + '</a>' +
      '<p class="sf-guarantee">' + esc(rt.under_button) + '</p>' +
      restart + '</div>'
    );
  };

  Finder.prototype.clear = function (keys) {
    for (var i = 0; i < keys.length; i++) delete this.a[keys[i]];
  };

  Finder.prototype.answer = function (step, value, button) {
    var a = this.a;
    if (step === 'location' || step === 'floor') {
      a[step] = value;
      this.go(step === 'location' ? 'floor' : 'shape');
    } else if (step === 'shape') {
      if (a.shape !== value) this.clear(['leg'].concat(SIZE_KEYS));
      a.shape = value;
      if (this.data.steps.leg.skip.indexOf(value) !== -1) {
        a.leg = '';
        this.afterLeg();
      } else {
        this.go('leg');
      }
    } else if (step === 'leg') {
      if (a.leg !== value) this.clear(SIZE_KEYS);
      a.leg = value;
      this.afterLeg();
    } else if (step === 'band' || step === 'pin') {
      a[step] = value;
      this.go('result');
    } else if (step === 'w' || step === 'd') {
      a[step] = value;
      var group = button.parentNode.querySelectorAll('[data-sf-step="' + step + '"]');
      for (var i = 0; i < group.length; i++) group[i].setAttribute('aria-pressed', String(group[i] === button));
      var show = this.body.querySelector('[data-sf-show]');
      if (show && a.w && a.d && show.disabled) {
        // Both measurements given: enable the button and bring it into view once.
        show.disabled = false;
        show.scrollIntoView({ block: 'nearest' });
      }
    }
  };

  /** After step 4 (or step 3 for wheels): a route with no cap ends here, otherwise ask for the size. */
  Finder.prototype.afterLeg = function () {
    this.go(routeFor(this.data, this.a) ? 'size' : 'result');
  };

  Finder.prototype.onClick = function (event) {
    var el = event.target.closest('[data-sf-step],[data-sf-back],[data-sf-close],[data-sf-restart],[data-sf-retry],[data-sf-show]');
    if (!el || !this.root.contains(el)) return;
    if (el.hasAttribute('data-sf-back')) return this.back();
    if (el.hasAttribute('data-sf-close')) return this.dialog && this.dialog.close();
    if (el.hasAttribute('data-sf-retry')) return this.start();
    if (!this.data) return;
    if (el.hasAttribute('data-sf-restart')) {
      this.reset();
      this.screen = null;
      return this.show('location');
    }
    if (el.hasAttribute('data-sf-show')) {
      if (this.a.w && this.a.d) this.go('result');
      return;
    }
    this.answer(el.getAttribute('data-sf-step'), el.getAttribute('data-sf-value'), el);
  };

  /* --- pop-up --- */

  var dialog = null;
  var dialogFinder = null;
  var returnFocusTo = null;

  function setupDialog() {
    var el = document.querySelector('dialog[data-size-finder-dialog]');
    if (!el || typeof el.showModal !== 'function') return;
    var app = el.querySelector('[data-size-finder-app]');
    if (!app) return;
    dialog = el;
    dialogFinder = new Finder(app, { src: el.getAttribute('data-size-finder-src'), dialog: el });

    el.addEventListener('close', function () {
      document.documentElement.classList.remove('sf-lock');
      var target = returnFocusTo;
      returnFocusTo = null;
      if (target && document.contains(target) && typeof target.focus === 'function') target.focus();
    });
    // A click on the backdrop (desktop) closes the pop-up; the panel fills the dialog box itself.
    el.addEventListener('click', function (event) {
      if (event.target === el) el.close();
    });
  }

  /** A trigger in the phone menu: close the menu first. Returns the menu's own button (for focus), or null. */
  function closeMenuOf(trigger) {
    var drawer = trigger && trigger.closest ? trigger.closest('header-drawer') : null;
    if (!drawer) return null;
    if (typeof drawer.close === 'function') {
      try {
        drawer.close();
      } catch (e) {
        /* the menu stays open under the guide */
      }
    }
    return drawer.querySelector('summary');
  }

  function openDialog(trigger) {
    if (!dialog) return false;
    if (dialog.open) return true;
    returnFocusTo = closeMenuOf(trigger) || trigger;
    document.documentElement.classList.add('sf-lock');
    dialog.showModal();
    dialogFinder.start();
    return true;
  }

  /* --- inline, on /pages/find-din-stoerrelse --- */

  var inlineRoot = null;
  var inlineFinder = null;

  function setupInline() {
    var el = document.querySelector('[data-size-finder-app][data-size-finder-inline]');
    if (!el || el === inlineRoot) return;
    inlineRoot = el;
    inlineFinder = new Finder(el, { src: el.getAttribute('data-size-finder-src') });
  }

  function startInline(trigger) {
    if (!inlineRoot || !document.contains(inlineRoot)) return false;
    closeMenuOf(trigger);
    inlineRoot.hidden = false;
    var starts = document.querySelectorAll('[data-size-finder-start]');
    for (var i = 0; i < starts.length; i++) starts[i].hidden = true;
    inlineFinder.start();
    if (!inlineFinder.data) inlineRoot.scrollIntoView({ block: 'start' });
    return true;
  }

  /* --- triggers --- */

  function isFinderLink(el) {
    if (el.tagName !== 'A' || !el.href || (el.target && el.target !== '_self') || el.hasAttribute('download')) return false;
    try {
      var url = new URL(el.href, window.location.href);
      return url.origin === window.location.origin && PAGE_PATH.test(url.pathname) && !url.hash;
    } catch (e) {
      return false;
    }
  }

  function onDocumentClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    var trigger = event.target.closest('[data-size-finder-open], a[href]');
    if (!trigger) return;
    if (!trigger.hasAttribute('data-size-finder-open') && !isFinderLink(trigger)) return;
    if (trigger.closest('[data-size-finder-app]')) return;

    if (startInline(trigger) || openDialog(trigger)) event.preventDefault();
  }

  function init() {
    setupDialog();
    setupInline();
    document.addEventListener('click', onDocumentClick);
    // Theme editor: the page section can be re-rendered.
    document.addEventListener('shopify:section:load', setupInline);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
